import { useEffect, useMemo, useState } from "react";
import { ScreenLoader, type Transport, type RuntimeHooks } from "@compath/renderer";
import type { Principal } from "../../controllers/oidc";
import type { OpenApp } from "../../models/types";
import { metaFor } from "../../models/catalog";

// Hosts a running add-on app: the renderer fetches the sibling's SDUI document through the BFF and
// drives it. The shell only supplies transport, identity, storage, and the platform hooks.
export function RunningApp({
  open, transport, principal, onClose,
}: {
  open: OpenApp; transport: Transport; principal: Principal; onClose: () => void;
}) {
  const [toast, setToast] = useState<string | null>(null);
  // The screen the sibling is currently showing. Starts at the app's entry screen and moves when the
  // sibling asks — a multi-screen app (e.g. VulnGuard's board ⇄ git 연동) is otherwise unreachable
  // past its first screen, because the renderer's navigate op is only as real as this hook.
  const [screen, setScreen] = useState(open.screen);
  useEffect(() => { setScreen(open.screen); }, [open.appId, open.screen]);
  const m = metaFor(open.appId);

  const hooks = useMemo<RuntimeHooks>(() => ({
    confirm: (msg) => window.confirm(msg),
    toast: (msg) => { setToast(String(msg)); window.setTimeout(() => setToast(null), 2500); },
    // Scoped to the running app on purpose: a sibling may move between its OWN screens, not open a
    // different app. Anything else is refused loudly rather than silently doing nothing.
    navigate: (to) => {
      const target = String(to).replace(/^\/+/, "");
      if (target === screen) return;
      if (target.split("/")[0] !== open.appId) {
        setToast(`이 앱 밖으로는 이동할 수 없습니다: ${target}`);
        window.setTimeout(() => setToast(null), 2500);
        return;
      }
      setScreen(target);
    },
    sign: (ref) => { console.log("sign requested", ref); return { signatureId: "sig_dev" }; },
    download: (filename, content, contentType) => {
      const text = typeof content === "string" ? content : JSON.stringify(content, null, 2);
      const blob = new Blob([text], { type: contentType || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
  }), [open.appId, screen]);

  const storage = useMemo(() => ({ session: window.sessionStorage, local: window.localStorage }), []);

  return (
    <div>
      <div className="app-bar">
        <div className="tab-chip">
          <span>{m.emoji}</span>
          <span>{open.name}</span>
          <button className="close" title="닫기" onClick={onClose}>✕</button>
        </div>
        <span className="crumb">{screen}</span>
        {screen !== open.screen && (
          <button className="link-btn" onClick={() => setScreen(open.screen)}>← 처음 화면</button>
        )}
      </div>
      <div className="app-stage">
        <ScreenLoader
          key={`${open.appId}/${screen}`}
          appId={open.appId}
          screen={screen}
          transport={transport}
          principal={principal}
          storage={storage}
          hooks={hooks}
          renderLoading={() => <div className="center">화면 불러오는 중…</div>}
          renderError={() => (
            <div className="center">이 화면을 불러오지 못했습니다. sibling이 실행 중인지 확인하세요.</div>
          )}
          renderPurchase={(app, url) => (
            <div className="center">구매가 필요합니다: {app}{url ? ` (${url})` : ""}</div>
          )}
        />
        <div className="app-note">
          <span>ⓘ</span>
          <span>
            이 화면은 <b>{open.appId}</b> sibling이 내려준 SDUI 문서를 compath 렌더러가 그린 것입니다.
            화면 하나 추가에 compath 코드 변경은 0줄.
          </span>
        </div>
      </div>
      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "#191f28", color: "#fff", padding: "10px 18px", borderRadius: 10, fontSize: 15, zIndex: 50 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
